import type { ListBuilderColumn } from '../../types';
import { PAYMENT_METHOD_ICON_MAPPINGS, STATUS_VALUE_MAPPINGS } from './shared';

/**
 * Transaction identifiers + origin column.
 *
 * Shows the three distinct identifiers a transaction carries — the CRM's own
 * Internal ID (the summary `_id`, also the row link), the originating ERP's id,
 * and the payment processor's id — followed by the Origin. Each line maps to a
 * stacked header (same pattern as the amounts column). ERP-origin transactions
 * have no processor id (and vice-versa) so the absent line renders `-`.
 */
export const createTransactionIdOriginColumn = (): ListBuilderColumn => ({
	id: 'transaction-id-origin',
	fields: [
		{
			id: 'transaction-id-field',
			link: {
				type: 'object',
				enabled: true,
				idField: '_id',
				objectTable: 'transaction_summary',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: '_id', objectTable: 'transaction_summary' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'transaction-erp-id-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'm_payment_externalId_erp',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				// Secondary lines (not the top/Internal-ID line) use textSecondary.
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-erp',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'transaction-processor-id-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'm_sm_payment_externalId',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-processor',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'transaction-origin-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'm_payment_origin',
					objectTable: 'transaction_summary',
				},
				defaultValue: 'CRM',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		// PD-14977 — the order this transaction paid for, linked. "Which order
		// was this for?" was previously unanswerable from the list: the field
		// was offered by the column picker but the API always returned null.
		{
			id: 'line-break-order',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'transaction-order-id-field',
			link: {
				type: 'object',
				enabled: true,
				idField: 'orderId',
				objectTable: 'order',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'orderId', objectTable: 'transaction_summary' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h1-internal-id', text: 'Internal ID' },
		{ id: 'h1-erp-id', text: 'ERP ID' },
		{ id: 'h1-processor-id', text: 'Processor ID' },
		{ id: 'h1-origin', text: 'Origin' },
		{ id: 'h1-order', text: 'Order' },
	],
	minWidth: 150,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{ direction: 'DESC', fieldName: '_id', objectTable: 'transaction_summary' },
	],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'top',
});

/**
 * Client info column
 */
export const createClientInfoColumn = (): ListBuilderColumn => ({
	id: 'client-info',
	fields: [
		{
			id: 'client-name-field',
			link: {
				type: 'object',
				enabled: true,
				idField: 'clientId',
				objectTable: 'client',
			},
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientName', objectTable: 'transaction_summary' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-2',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'client-address-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'clientAddress',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				// `sm` + theme `textSecondary` to match every other column's
				// secondary line. ('xxs' isn't in SIZE_MAP, so it silently fell
				// back to `md` — rendering these lines larger than their siblings.)
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-address',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'client-city-state-field',
			dataConfig: {
				type: 'template',
				template: '{clientCity}, {clientState} {clientZipCode}',
				templateFields: [
					{ fieldName: 'clientCity', objectTable: 'transaction_summary' },
					{ fieldName: 'clientState', objectTable: 'transaction_summary' },
					{ fieldName: 'clientZipCode', objectTable: 'transaction_summary' },
				],
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h2', text: 'Client' }],
	minWidth: 180,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'clientName',
			objectTable: 'transaction_summary',
		},
	],
	widthConfig: { mode: 'flexible', preset: 'large' },
	verticalAlignment: 'top',
});

/**
 * Payment method column
 */
export const createPaymentMethodColumn = (): ListBuilderColumn => ({
	id: 'payment-method',
	fields: [
		{
			id: 'payment-method-icon-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'm_payment_payment_method_type',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'value-mapping' },
				valueMapping: [...PAYMENT_METHOD_ICON_MAPPINGS],
			},
			displayStyle: 'icon',
		},
		{
			id: 'payment-method-subtype-field',
			dataConfig: {
				type: 'conditional',
				conditions: [
					{
						field: 'm_payment_payment_method_type',
						value: 'check',
						operator: 'equals',
						dataConfig: {
							type: 'field',
							field: {
								fieldName: 'm_payment_checkNumber',
								objectTable: 'transaction_summary',
							},
							templateFields: [
								{
									fieldName: 'm_payment_payment_method_subtype',
									objectTable: 'transaction_summary',
								},
								{
									fieldName: 'm_payment_checkNumber',
									objectTable: 'transaction_summary',
								},
							],
						},
					},
					{
						field: 'm_payment_payment_method_type',
						value: 'cash',
						operator: 'equals',
						dataConfig: { type: 'custom_text', customText: '' },
					},
				],
				defaultDataConfig: {
					type: 'template',
					template:
						'{m_payment_payment_method_subtype} - {m_payment_payment_method_lastFour}',
					templateFields: [
						{
							fieldName: 'm_payment_payment_method_subtype',
							objectTable: 'transaction_summary',
						},
						{
							fieldName: 'm_payment_payment_method_lastFour',
							objectTable: 'transaction_summary',
						},
					],
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-3',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'payment-method-type-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'm_payment_payment_method_type',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-4',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'payment-method-name-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'm_payment_payment_method_name',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h3', text: 'Payment Method' }],
	minWidth: 180,
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'large' },
	verticalAlignment: 'top',
});

/**
 * Transaction amounts column
 */
export const createTransactionAmountsColumn = (): ListBuilderColumn => ({
	id: 'transaction-amounts',
	fields: [
		{
			id: 'amount-paid-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'm_sm_payment_amount',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
			},
			displayStyle: 'currency',
		},
		{
			id: 'line-break-5',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'net-amount-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'm_payment_netAmount',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
		{
			id: 'line-break-6',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'processing-fee-field',
			// PD-15077 — the fee in dollars, computed server-side from the
			// transaction's rate and unit. This was a subtraction of net amount
			// from amount paid, which reads zero on a capture (the two are
			// equal) and a flat rate on an authorization, so it reported no fee
			// on payments that carried one.
			dataConfig: {
				type: 'field',
				field: { fieldName: 'm_payment_feeCalculated' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'currency',
		},
	],
	headers: [
		{ id: 'h4-amount-paid', text: 'Amount Paid' },
		{ id: 'h4-net-amount', text: 'Net Amount' },
		{ id: 'h4-processing-fee', text: 'Processing Fee' },
	],
	minWidth: 150,
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'm_sm_payment_amount',
			objectTable: 'transaction_summary',
		},
	],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Transaction dates column
 */
export const createTransactionDatesColumn = (): ListBuilderColumn => ({
	id: 'transaction-dates',
	fields: [
		{
			id: 'sale-date-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'm_payment_datetime',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
				// Keep the date/time on one line — "6/22/26 12:00 AM" was wrapping
				// the meridiem ("AM") onto a second line in narrow columns.
				allowTextWrap: false,
				// Match the orders list date format (e.g. "4/2/26 5:16 PM").
				formatOptions: { dateFormat: 'datetime-short' },
			},
			displayStyle: 'date',
		},
		{
			id: 'line-break-7',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'refund-date-field',
			// PD-15450 — the refund's own timestamp beside the original sale
			// date. The transactions endpoint flattens the latest succeeded
			// refund's datetime onto rows as `refundDate`; rows with no refund
			// fall back to the shared '-' default.
			dataConfig: {
				type: 'field',
				field: { fieldName: 'refundDate', objectTable: 'transaction_summary' },
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				allowTextWrap: false,
				formatOptions: { dateFormat: 'datetime-short' },
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{ id: 'h5-sale-date', text: 'Sale Date' },
		{ id: 'h5-refund-date', text: 'Refund Date' },
	],
	minWidth: 160,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'm_payment_datetime',
			objectTable: 'transaction_summary',
		},
	],
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
});

/**
 * Status column
 */
export const createStatusColumn = (): ListBuilderColumn => ({
	id: 'status',
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'm_payment_status',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'value-mapping' },
				valueMapping: [...STATUS_VALUE_MAPPINGS],
			},
			displayStyle: 'badge',
		},
		// PD-14977 — the kind of transaction, on its own line under the status.
		// It already reached the API and was list-builder-pickable, but shipped
		// hidden, so an authorization and a capture looked identical here: the
		// default view could not distinguish money merely held from money
		// actually taken.
		{
			id: 'status-line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
		},
		{
			id: 'transaction-type-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'transactionType',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
		// PD-14977 — and whether the funds were actually collected. This is the
		// field the whole reconciliation turns on: an approved authorization
		// reads as a success everywhere else in the UI while the money has only
		// been held, never taken.
		{
			id: 'status-line-break-2',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
			// PD-15477 — see the funds field below; the break hides with it.
			conditionalVisibility: {
				field: 'm_payment_status',
				condition: 'not_in',
				value: ['Failed', 'Error', 'Declined', 'Cancelled', 'Voided'],
			},
		},
		{
			id: 'funds-collected-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'isSettled',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'value-mapping' },
				valueMapping: [
					// PD-15194 — the money WAS collected when the charge was
					// approved; it is the DEPOSIT that is pending. "Not
					// collected" beside a Succeeded status read as a
					// contradiction (owner report).
					{
						value: 'true',
						color: { themeColor: 'success' },
						label: 'Settled',
					},
					{
						value: 'false',
						color: { themeColor: 'warning' },
						label: 'Pending settlement',
					},
				],
			},
			displayStyle: 'text',
			// PD-15477 — the funds line only makes sense where money actually
			// moved (or still can). On a failed, declined, cancelled, errored,
			// or voided transaction nothing is coming, so "Pending settlement"
			// was a false promise; the status badge above already tells the
			// story on those rows.
			conditionalVisibility: {
				field: 'm_payment_status',
				condition: 'not_in',
				value: ['Failed', 'Error', 'Declined', 'Cancelled', 'Voided'],
			},
		},
		{
			id: 'refund-settlement-line-break',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'line_break',
			conditionalVisibility: {
				field: 'refundIsSettled',
				condition: 'is_not_null',
			},
		},
		{
			// PD-15450 — the refund's settlement state beside the original's.
			// `refundIsSettled` is null when the chain has no refunds (field
			// hidden), false while NMI has the refund pending, true once the
			// PD-15008 settlement sweep confirms it settled.
			id: 'refund-settlement-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'refundIsSettled',
					objectTable: 'transaction_summary',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'value-mapping' },
				valueMapping: [
					{
						value: 'true',
						color: { themeColor: 'success' },
						label: 'Refund settled',
					},
					{
						value: 'false',
						color: { themeColor: 'warning' },
						label: 'Refund pending settlement',
					},
				],
			},
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'refundIsSettled',
				condition: 'is_not_null',
			},
		},
	],
	headers: [
		{ id: 'h6', text: 'Status' },
		{ id: 'h6b', text: 'Type' },
		{ id: 'h6c', text: 'Funds' },
	],
	minWidth: 140,
	sortable: true,
	alignment: 'center',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'm_payment_status',
			objectTable: 'transaction_summary',
		},
	],
	widthConfig: { mode: 'flexible', preset: 'small' },
	verticalAlignment: 'center',
});

/**
 * Actions column
 */
export const createActionsColumn = (): ListBuilderColumn => ({
	id: 'actions',
	fields: [
		{
			id: 'actions-dropdown',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'void-action',
					label: 'Void',
					iconName: 'ban',
					callbackName: 'void',
					variant: 'danger',
					visibility: 'visible',
					visibilityCallback: 'shouldShowVoidButton',
				},
				{
					id: 'refund-action',
					label: 'Refund',
					iconName: 'rotate-left',
					callbackName: 'refund',
					variant: 'warning',
					visibility: 'visible',
					visibilityCallback: 'shouldShowRefundButton',
				},
			],
		},
	],
	headers: [{ id: 'h7', text: '' }],
	width: 80,
	align: 'center',
	sortable: false,
});

/**
 * Create all transaction summary columns
 */
export const createTransactionColumns = (): Array<ListBuilderColumn> => [
	createTransactionIdOriginColumn(),
	createClientInfoColumn(),
	createPaymentMethodColumn(),
	createTransactionAmountsColumn(),
	createTransactionDatesColumn(),
	createStatusColumn(),
	createActionsColumn(),
];
